/**
 * Pure flow rules for the strategy questionnaire (no React, no side effects).
 *
 * Belongs here: back/advance/clear-answers decisions given cursor + payload.
 * Does not belong here: navigation calls, API submit, or Rive playback.
 */
import type {
  StrategyQuestionnaireAnswersMap,
  StrategyQuestionnaireStage,
  Question,
  ClearAnswersOptions
} from '../../shared/types/questionnaire.types'
import { ClearAnswersOnEditMode } from '../../shared/types/questionnaire.types'
import { getVisibleQuestions, findNextVisibleQuestionIndex } from '../../question-utils/visibleQuestions'

// ─── Clear answers on edit ───────────────────────────────────────────────────

/**
 * Which answer keys to remove for a given clear mode.
 * Stage / all-stage wipes ignore visibility; after-this modes follow visible walk order.
 */
export function getQuestionIdsToClearForStrategy(
  options: ClearAnswersOptions | undefined,
  stages: StrategyQuestionnaireStage[],
  answers: StrategyQuestionnaireAnswersMap,
  mode: ClearAnswersOnEditMode
): string[] {
  if (mode === ClearAnswersOnEditMode.NONE) return []
  // CLEAR_ALL_STAGES only needs answer keys — stages list optional
  if (!stages?.length && mode !== ClearAnswersOnEditMode.CLEAR_ALL_STAGES) return []
  if (mode === ClearAnswersOnEditMode.CLEAR_ALL_STAGES) return Object.keys(answers)

  const { questionId, stageId } = options ?? {}

  if (mode === ClearAnswersOnEditMode.CLEAR_ALL_AFTER_THIS && questionId) {
    const orderedIds: string[] = []
    for (const stage of stages) {
      for (const question of getVisibleQuestions(stage, answers)) {
        if (question?.id) orderedIds.push(question.id)
      }
    }
    const questionPosition = orderedIds.indexOf(questionId)
    return questionPosition === -1 ? [] : orderedIds.slice(questionPosition + 1)
  }

  if (mode === ClearAnswersOnEditMode.CLEAR_CURRENT_STAGE_AFTER_THIS && questionId) {
    for (const stage of stages) {
      const visible = getVisibleQuestions(stage, answers)
      const orderedIds = visible.map((question) => question?.id).filter(Boolean) as string[]
      const questionPosition = orderedIds.indexOf(questionId)
      if (questionPosition !== -1) return orderedIds.slice(questionPosition + 1)
    }
    return []
  }

  if (mode === ClearAnswersOnEditMode.CLEAR_CURRENT_STAGE) {
    // Stage edit wipe — every question in the stage, not only currently visible
    if (stageId) {
      const stage = stages.find((stageItem) => stageItem.id === stageId)
      if (!stage) return []
      return (stage.questions ?? [])
        .map((question) => question?.id)
        .filter(Boolean) as string[]
    }
    if (questionId) {
      for (const stage of stages) {
        const questionIds = (stage.questions ?? [])
          .map((question) => question?.id)
          .filter(Boolean) as string[]
        if (questionIds.includes(questionId)) return questionIds
      }
    }
  }

  return []
}

/** Edit rule: return answers after clearing for edit (one stage, all stages, or downstream). */
export function getAnswersAfterEdit(
  previousAnswers: StrategyQuestionnaireAnswersMap,
  mode: ClearAnswersOnEditMode,
  options: ClearAnswersOptions | undefined,
  stages: StrategyQuestionnaireStage[]
): StrategyQuestionnaireAnswersMap {
  const questionIdsToClear = getQuestionIdsToClearForStrategy(options, stages, previousAnswers, mode)
  const nextAnswers = { ...previousAnswers }
  questionIdsToClear.forEach((questionId) => delete nextAnswers[questionId])
  return nextAnswers
}

/**
 * Add rule: set answer and clear downstream visible questions when value changes.
 * Pass active session stages (full questionnaire or single stage when editing).
 */
export function getAnswersAfterAdd(
  previousAnswers: StrategyQuestionnaireAnswersMap,
  questionId: string,
  value: string | number,
  stages: StrategyQuestionnaireStage[],
  clearMode: ClearAnswersOnEditMode = ClearAnswersOnEditMode.CLEAR_ALL_AFTER_THIS
): StrategyQuestionnaireAnswersMap {
  const nextAnswers = { ...previousAnswers, [questionId]: value }
  // Changing an earlier answer invalidates downstream answers in visible order
  if (questionId in previousAnswers && value !== previousAnswers[questionId] && stages.length > 0) {
    const questionIdsToClear = getQuestionIdsToClearForStrategy(
      { questionId },
      stages,
      nextAnswers,
      clearMode
    )
    questionIdsToClear.forEach((clearedId) => delete nextAnswers[clearedId])
  }
  return nextAnswers
}

// ─── Back ────────────────────────────────────────────────────────────────────

/** Where getStrategyBackTarget sends the cursor — or BrowserBack to leave answering. */
export enum StrategyBackTargetType {
  PrevQuestion = 'prev_question',
  PrevStageLast = 'prev_stage_last',
  BrowserBack = 'browser_back'
}

/** Discriminated back target — page-flow calls goToQuestion or cancelAnswering. */
export type StrategyBackTarget =
  | { type: StrategyBackTargetType.PrevQuestion; stageIndex: number; questionIndex: number }
  | { type: StrategyBackTargetType.PrevStageLast; stageIndex: number; questionIndex: number }
  | { type: StrategyBackTargetType.BrowserBack }

/**
 * Where back should go: previous question, previous stage's last question, or leave page.
 *
 * @param questionIndex — current index in visible questions for this stage
 * @param stageIndex — index into `stages`
 * @param stages — `questionnaireData.stages` from API
 * @param answers — current draftAnswers (for conditional visibility on prev stage)
 */
export function getStrategyBackTarget(
  questionIndex: number,
  stageIndex: number,
  stages: StrategyQuestionnaireStage[],
  answers: StrategyQuestionnaireAnswersMap
): StrategyBackTarget {
  // Same stage — walk to previous visible question
  if (questionIndex > 0) {
    return {
      type: StrategyBackTargetType.PrevQuestion,
      stageIndex,
      questionIndex: questionIndex - 1
    }
  }
  if (stageIndex > 0 && stages.length > 0) {
    const previousStage = stages[stageIndex - 1]
    if (!previousStage) return { type: StrategyBackTargetType.BrowserBack }
    // First question in stage — land on last visible question of previous stage
    const previousVisible = getVisibleQuestions(previousStage, answers)
    const lastQuestionIndex = Math.max(0, previousVisible.length - 1)
    return {
      type: StrategyBackTargetType.PrevStageLast,
      stageIndex: stageIndex - 1,
      questionIndex: lastQuestionIndex
    }
  }
  return { type: StrategyBackTargetType.BrowserBack }
}

// ─── Advance after answer ────────────────────────────────────────────────────

/** Where getStrategyNextAfterAnswer says to go after AnswerQuestion commits. */
export enum StrategyNextAfterAnswerTargetType {
  NextQuestion = 'next_question',
  NextStage = 'next_stage',
  Review = 'review'
}

/** Discriminated advance target — session hook maps to StrategyConfirmOutcome + page result. */
export type StrategyNextAfterAnswerTarget =
  | { type: StrategyNextAfterAnswerTargetType.NextQuestion; stageIndex: number; questionIndex: number }
  | { type: StrategyNextAfterAnswerTargetType.NextStage; nextStageIndex: number }
  | { type: StrategyNextAfterAnswerTargetType.Review }

/** Inputs for advance-after-answer — visibility lists must reflect draft after the new answer. */
export interface GetStrategyNextAfterAnswerParams {
  stageIndex: number
  questionIndex: number
  currentQuestionId: string | undefined
  visibleQuestions: Question[]
  visibleAfterAnswer: Question[]
  isLastStage: boolean
}

/**
 * Where to go after user answers the current question.
 * Returns next question, next stage boundary, or review. Handler calls onStageFinished on stage/review.
 *
 * @param params.stageIndex / questionIndex — current cursor
 * @param params.visibleQuestions — visible list before answer
 * @param params.visibleAfterAnswer — visible list after answer (conditionals may change)
 * @param params.isLastStage — true when on final stage in payload
 */
export function getStrategyNextAfterAnswer(
  params: GetStrategyNextAfterAnswerParams
): StrategyNextAfterAnswerTarget {
  const {
    stageIndex,
    questionIndex,
    currentQuestionId,
    visibleQuestions,
    visibleAfterAnswer,
    isLastStage
  } = params

  const nextVisible = findNextVisibleQuestionIndex(
    visibleQuestions,
    visibleAfterAnswer,
    currentQuestionId,
    questionIndex
  )

  if (nextVisible != null) {
    return {
      type: StrategyNextAfterAnswerTargetType.NextQuestion,
      stageIndex,
      questionIndex: nextVisible.nextIndex
    }
  }

  // No more visible questions in this stage
  if (!isLastStage) {
    return { type: StrategyNextAfterAnswerTargetType.NextStage, nextStageIndex: stageIndex + 1 }
  }

  return { type: StrategyNextAfterAnswerTargetType.Review }
}
